import {
  allTenantsSelector,
  userTenantsSelector,
} from 'app/plugins/account/components/userManagement/tenants/selectors'
import getDataSelector from 'core/utils/getDataSelector'
import { createSharedSelector } from 'core/utils/selectorHelpers'
import DataKeys from 'k8s/DataKeys'
import { emptyObj } from 'utils/fp'
import { latestMessageByVolumeSelector } from '../messages/selectors'
import { getBackingImageId } from './utils/volumeUtils'

const emptyMessageMap = {}

export const volumeTransfersSelector = createSharedSelector(
  getDataSelector<DataKeys.VolumeTransfers>(DataKeys.VolumeTransfers),
  (volumeTransfers) => {
    return volumeTransfers
  },
)

export const allVolumeTransfersSelector = createSharedSelector(
  getDataSelector<DataKeys.AllVolumeTransfers>(DataKeys.AllVolumeTransfers),
  (volumeTransfers) => {
    return volumeTransfers
  },
)

export const volumesSelector = createSharedSelector(
  getDataSelector<DataKeys.OpenstackVolumes>(DataKeys.OpenstackVolumes),
  allTenantsSelector,
  userTenantsSelector,
  latestMessageByVolumeSelector,
  (volumes, allTenants, userTenants, latestMessageByVolume = emptyMessageMap) => {
    return volumes.map((volume) => {
      return {
        ...volume,
        name: volume?.name || volume?.id,
        tenantName: [...userTenants, ...allTenants]?.find(
          (tenant) => tenant.id === volume?.['os-vol-tenant-attr:tenant_id'],
        )?.name,
        message: latestMessageByVolume[volume?.id], // with poonams change this might be unneeded
      }
    })
  },
)

export const allVolumesSelector = createSharedSelector(
  getDataSelector<DataKeys.AllOpenstackVolumes>(DataKeys.AllOpenstackVolumes),
  latestMessageByVolumeSelector,
  (volumes, latestMessageByVolume = emptyMessageMap) => {
    return volumes.map((volume) => {
      return {
        ...volume,
        message: latestMessageByVolume[volume?.id],
      }
    })
  },
)

export const singleVolumeSelector = createSharedSelector(
  getDataSelector<DataKeys.SingleOpenstackVolume>(DataKeys.SingleOpenstackVolume),
  allTenantsSelector,
  userTenantsSelector,
  latestMessageByVolumeSelector,
  (volumes, allTenants, userTenants, latestMessageByVolume = emptyMessageMap) => {
    return volumes.map((volume) => {
      return {
        ...volume,
        name: volume?.name || volume?.id,
        tenantName: [...userTenants, ...allTenants]?.find(
          (tenant) => tenant.id === volume?.['os-vol-tenant-attr:tenant_id'],
        )?.name,
        message: latestMessageByVolume[volume?.id],
      }
    })
  },
)

export const volumesLiteSelector = createSharedSelector(
  getDataSelector<DataKeys.OpenstackVolumesLite>(DataKeys.OpenstackVolumesLite),
  (vms) => vms,
)

export const volumesLiteByIdSelector = createSharedSelector(volumesLiteSelector, (vms) =>
  vms.reduce((accum, vm) => {
    accum[vm.id] = vm
    return accum
  }, {}),
)

export const volumesByIdSelector = createSharedSelector(volumesSelector, (volumes) => {
  return volumes.reduce((accum, volume) => {
    accum[volume.id] = volume
    return accum
  }, {})
})

export const nonPaginatedVolumesSelector = createSharedSelector(
  getDataSelector<DataKeys.NonPaginatedOpenstackVolumes>(DataKeys.NonPaginatedOpenstackVolumes),
  (volumes) => {
    return volumes.map((volume) => {
      return {
        ...volume,
        name: volume?.name || volume?.id,
        multiattach: volume?.multiattach,
      }
    })
  },
)

export const availableVolumesSelector = createSharedSelector(
  getDataSelector<DataKeys.AvailableOpenstackVolumes>(DataKeys.AvailableOpenstackVolumes),
  allTenantsSelector,
  userTenantsSelector,
  latestMessageByVolumeSelector,
  (volumes, allTenants, userTenants, latestMessageByVolume = emptyMessageMap) => {
    return volumes.map((volume) => {
      return {
        ...volume,
        name: volume?.name || volume?.id,
        multiattach: volume?.multiattach,
        tenantName: [...userTenants, ...allTenants]?.find(
          (tenant) => tenant.id === volume?.['os-vol-tenant-attr:tenant_id'],
        )?.name,
        message: latestMessageByVolume[volume?.id], // with poonams change this might be unneeded
      }
    })
  },
)

export const nonPaginatedVolumesByIdSelector = createSharedSelector(
  nonPaginatedVolumesSelector,
  (volumes) => {
    return volumes.reduce((accum, volume) => {
      accum[volume.id] = volume
      return accum
    }, {})
  },
)

export const volumesForImagesSelector = createSharedSelector(
  getDataSelector<DataKeys.OpenstackVolumesForImages>(DataKeys.OpenstackVolumesForImages),
  (volumes) => {
    return volumes.map((volume) => {
      return {
        ...volume,
        name: volume?.name || volume?.id,
      }
    })
  },
)

// VOLSEL-FR-006, VOLSEL-FR-007: Group by metadata.glance_image_id only, push in place
export const volumesByImageIdSelector = createSharedSelector(volumesForImagesSelector, (volumes) =>
  volumes.reduce<Record<string, typeof volumes>>((acc, vol) => {
    const imageId = getBackingImageId(vol)
    if (!imageId) return acc
    if (!acc[imageId]) acc[imageId] = []
    acc[imageId].push(vol)
    return acc
  }, {}),
)
